import {
  paiseAmountToRupee,
  toIndianCurrencyString,
  bpsToPercentage,
  percentageToBps,
} from 'utils/Numbers';
import { ComplianceBehaviourKey, FlexibleBenefitFormData, TaxBehaviour } from './Create/types';
import { CreateFlexibleBenefitInput } from 'api/gql-utils/flexibleBenefits/types';
import { FBP_BPS_PRECISION } from './constants';

type WageCalculationToggle = {
  label: string;
  behaviourKey: ComplianceBehaviourKey;
  field: string;
  isChecked: boolean;
};

export const getWageCalculationToggles = (
  formData: FlexibleBenefitFormData,
  includeBaseCompensationBehaviour = true,
): Array<WageCalculationToggle> => {
  const toggles: Array<WageCalculationToggle> = [
    {
      label: 'Part of PF wage',
      behaviourKey: 'pf_behaviour',
      field: 'include_in_pf_wages',
      isChecked: formData?.pf_behaviour?.include_in_pf_wages || false,
    },
    {
      label: 'Part of ESI wage',
      behaviourKey: 'esi_behaviour',
      field: 'include_in_esi_wages',
      isChecked: formData?.esi_behaviour?.include_in_esi_wages || false,
    },
    {
      label: 'Part of PT wage',
      behaviourKey: 'pt_behaviour',
      field: 'include_in_pt_wages',
      isChecked: formData?.pt_behaviour?.include_in_pt_wages || false,
    },
    {
      label: 'Part of LWF wage',
      behaviourKey: 'lwf_behaviour',
      field: 'include_in_lwf_wages',
      isChecked: formData?.lwf_behaviour?.include_in_lwf_wages || false,
    },
    {
      label: 'Part of Gratuity wages',
      behaviourKey: 'gratuity_behaviour',
      field: 'include_in_gratuity_wages',
      isChecked: formData?.gratuity_behaviour?.include_in_gratuity_wages || false,
    },
    {
      label: 'Part of Leave Encashment wages',
      behaviourKey: 'leave_encashment_behaviour',
      field: 'include_in_leave_encashment_wages',
      isChecked: formData?.leave_encashment_behaviour?.include_in_leave_encashment_wages || false,
    },
  ];

  if (includeBaseCompensationBehaviour) {
    toggles.push({
      label: 'Part of Base Compensation',
      behaviourKey: 'base_compensation_behaviour',
      field: 'include_in_base_compensation',
      isChecked: formData?.base_compensation_behaviour?.include_in_base_compensation || false,
    });
  }

  return toggles;
};

const getPercentageValueForPrecision = (
  value: number,
  responsePrecision: number | undefined,
): number => (responsePrecision === FBP_BPS_PRECISION ? bpsToPercentage(value) : value);

export const formatBenefitAmount = (
  benefitSettings?: {
    type: string;
    value?: number;
    unit?: string;
    options?: number[];
    list_of_options_amount?: {
      amounts: number[];
      default_option_index: number;
      allow_multiple: boolean;
      amount_labels: string[];
    };
    fixed_amount?: {
      amount: number;
      description: string;
    };
    range_amount?: {
      max: number;
      min: number;
    };
  },
  responsePrecision?: number,
): { displayValue: string; description: string } => {
  if (!benefitSettings) {
    return { displayValue: '--', description: '' };
  }

  const { unit } = benefitSettings;
  const isPercentage = unit?.toLowerCase() === 'percentage_of_basic';
  const toDisplayValue = (value: number) =>
    isPercentage
      ? getPercentageValueForPrecision(value, responsePrecision)
      : paiseAmountToRupee(value);

  if (
    benefitSettings.list_of_options_amount &&
    benefitSettings.list_of_options_amount.amounts.length > 0
  ) {
    const formattedOptions = benefitSettings.list_of_options_amount.amounts
      .map((opt: number) => toIndianCurrencyString(paiseAmountToRupee(opt)))
      .join(', ');
    return {
      displayValue: isPercentage ? `${formattedOptions} of Basic` : formattedOptions,
      description: 'Multiple Values',
    };
  } else if (benefitSettings.fixed_amount) {
    const val = toDisplayValue(benefitSettings.fixed_amount.amount);
    const formattedValue = isPercentage
      ? `${val}% of Basic`
      : toIndianCurrencyString(val as number);
    return { displayValue: formattedValue, description: 'Max Limit' };
  } else if (benefitSettings.range_amount) {
    const val = toDisplayValue(benefitSettings.range_amount.max);
    const formattedValue = isPercentage
      ? `${val}% of Basic`
      : toIndianCurrencyString(val as number);
    return { displayValue: formattedValue, description: 'Max Limit' };
  }

  return { displayValue: '--', description: '' };
};

/**
 * Converts API response format to form data format for editing
 * Transforms nested API structure to flattened form structure
 */
export const convertApiResponseToFormData = (apiData: any): FlexibleBenefitFormData | null => {
  const settings = apiData?.settings || {};

  if (!apiData) {
    return null;
  }

  const responsePrecision: number | undefined = apiData.response_precision;
  const isPercentage = settings.benefit_amount_settings?.unit === 'PERCENTAGE_OF_BASIC';

  const rawMax =
    settings.benefit_amount_settings?.range_amount?.max ||
    settings.benefit_amount_settings?.fixed_amount?.amount ||
    0;

  return {
    id: apiData.id,
    name: apiData.name,
    isActive: apiData.is_active,
    componentType: apiData.component_type,
    displayName: settings.display_behaviour?.display_name || apiData.name,
    description: apiData.description || '',
    showInGrossSalary: settings.salary_settings?.show_in_gross_salary || false,
    nonCashBenefit: settings.salary_settings?.non_cash_benefit || false,

    declarationMethod: settings.declaration_settings?.declaration_enabled ? 'true' : 'false',

    declaration_settings: {
      declaration_enabled: settings.declaration_settings?.declaration_enabled ? 'true' : 'false',
      type: settings.declaration_settings?.type || 'FIXED',
    },

    benefit_amount_settings: {
      type: settings.benefit_amount_settings?.list_of_options_amount
        ? 'LIST_OF_VALUES'
        : settings.benefit_amount_settings?.range_amount
        ? 'RANGE'
        : 'FIXED',
      unit: settings.benefit_amount_settings?.unit || 'ACTUAL_AMOUNT',
      value: isPercentage
        ? getPercentageValueForPrecision(rawMax, responsePrecision)
        : paiseAmountToRupee(rawMax),
      options:
        settings?.benefit_amount_settings?.list_of_options_amount?.amounts.map((amount: number) =>
          paiseAmountToRupee(amount),
        ) || [],
      default_option_index:
        settings?.benefit_amount_settings?.list_of_options_amount?.default_option_index || 0,
    },

    tax_behaviour: {
      tax_exempt_in_old_regime: settings.tax_behaviour?.tax_exempt_in_old_regime || false,
      tax_exempt_in_new_regime: settings.tax_behaviour?.tax_exempt_in_new_regime || false,
      tax_exemption_section: settings.tax_behaviour?.tax_exemption_section || '',
      tax_deduction_behaviour: settings.tax_behaviour?.tax_deduction_behaviour || 'instant',
    },

    arrear_behaviour: {
      consider_in_arrear: settings.arrear_behaviour?.consider_in_arrear || false,
    },
    proration_behaviour: {
      support_proration: settings.proration_behaviour?.support_proration || false,
    },

    hold_settings: {
      hold_unclaimed_amount: settings.hold_settings?.hold_unclaimed_amount ? 'true' : 'false',
    },

    // Wage calculation (compliance) behaviours
    pf_behaviour: {
      include_in_pf_wages: settings.pf_behaviour?.include_in_pf_wages || false,
    },
    esi_behaviour: {
      include_in_esi_wages: settings.esi_behaviour?.include_in_esi_wages || false,
    },
    pt_behaviour: {
      include_in_pt_wages: settings.pt_behaviour?.include_in_pt_wages || false,
    },
    lwf_behaviour: {
      include_in_lwf_wages: settings.lwf_behaviour?.include_in_lwf_wages || false,
    },
    gratuity_behaviour: {
      include_in_gratuity_wages: settings.gratuity_behaviour?.include_in_gratuity_wages || false,
    },
    leave_encashment_behaviour: {
      include_in_leave_encashment_wages:
        settings.leave_encashment_behaviour?.include_in_leave_encashment_wages || false,
    },
    base_compensation_behaviour: {
      include_in_base_compensation:
        settings.base_compensation_behaviour?.include_in_base_compensation || false,
    },

    // Display Details
    showZero: settings.display_behaviour?.show_zero || false,
    visibleToEmployee: settings.display_behaviour?.visible_to_employee || false,
  };
};

/**
 * Converts flattened form data to the Flexible Benefits API payload format
 * Similar to convertFormDataToEarningSalaryComponentPayload in WizardViews
 */
export const convertFormDataToFlexiBenefitPayload = (
  formData: FlexibleBenefitFormData,
  id?: string,
  isFbpPercentagePrecisionEnabled = false,
  includeBaseCompensationBehaviour = true,
): CreateFlexibleBenefitInput => {
  const shouldForceHoldUnclaimedAmount = !Boolean(formData.showInGrossSalary);

  // Build tax behaviour object

  const isPercentageOfBasicValue = formData.benefit_amount_settings?.unit === 'PERCENTAGE_OF_BASIC';
  const toPercentageValue = (value: number) =>
    isFbpPercentagePrecisionEnabled ? percentageToBps(value) : value;

  const taxBehaviour: TaxBehaviour = {
    tax_exempt_in_old_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_old_regime),
    tax_exempt_in_new_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_new_regime),
  };

  // Add tax exemption section if any regime is exempt
  if (
    formData.tax_behaviour?.tax_exempt_in_old_regime ||
    formData.tax_behaviour?.tax_exempt_in_new_regime
  ) {
    taxBehaviour.tax_exemption_section = formData.tax_behaviour?.tax_exemption_section || '';
  }

  // Add tax deduction behaviour
  taxBehaviour.tax_deduction_behaviour =
    formData.tax_behaviour?.tax_deduction_behaviour || 'instant';

  let benefitAmountSettings: CreateFlexibleBenefitInput['salary_component_requests'][number]['settings']['benefit_amount_settings'];

  if (formData.benefit_amount_settings?.type === 'LIST_OF_VALUES') {
    const options = formData.benefit_amount_settings.options || [];
    benefitAmountSettings = {
      unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
      type: formData.benefit_amount_settings.type,
      list_of_options_amount: {
        amounts: options.map((amount: number) => amount * 100),
        default_option_index: formData.benefit_amount_settings.default_option_index || 0,
        allow_multiple: false,
        amount_labels: [],
      },
    };
  } else if (formData.benefit_amount_settings) {
    if (formData.benefit_amount_settings.type === 'RANGE') {
      benefitAmountSettings = {
        unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
        type: formData.benefit_amount_settings.type || 'RANGE',
        range_amount: {
          max:
            (isPercentageOfBasicValue
              ? toPercentageValue(Number(formData.benefit_amount_settings.value))
              : Number(formData.benefit_amount_settings.value) * 100) || 0,
          min: 0,
          step: isPercentageOfBasicValue ? toPercentageValue(1) : 1,
        },
      };
    } else {
      benefitAmountSettings = {
        unit: formData.benefit_amount_settings.unit || 'ACTUAL_AMOUNT',
        type: formData.benefit_amount_settings.type || 'FIXED',
        fixed_amount: {
          amount:
            (isPercentageOfBasicValue
              ? toPercentageValue(Number(formData.benefit_amount_settings.value))
              : Number(formData.benefit_amount_settings.value) * 100) || 0,
          description: '',
        },
      };
    }
  }

  const settings = {
    salary_settings: {
      show_in_gross_salary: Boolean(formData.showInGrossSalary),
      non_cash_benefit: Boolean(formData.nonCashBenefit),
    },
    benefit_amount_settings: benefitAmountSettings,
    declaration_settings: {
      declaration_enabled: formData.declaration_settings?.declaration_enabled === 'true',
      type: formData.declaration_settings?.type || 'RANGE',
    },
    hold_settings: {
      hold_unclaimed_amount: shouldForceHoldUnclaimedAmount
        ? true
        : formData.hold_settings?.hold_unclaimed_amount === 'true',
    },
    tax_behaviour: {
      tax_exempt_in_old_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_old_regime),
      tax_exempt_in_new_regime: Boolean(formData.tax_behaviour?.tax_exempt_in_new_regime),
      tax_exemption_section: formData.tax_behaviour?.tax_exemption_section || '',
      tax_deduction_behaviour: formData.tax_behaviour?.tax_deduction_behaviour || 'instant',
    },
    arrear_behaviour: {
      consider_in_arrear: Boolean(formData.arrear_behaviour?.consider_in_arrear),
      allow_negative: Boolean(formData.arrear_behaviour?.allow_negative),
    },
    proration_behaviour: {
      support_proration: formData?.proration_behaviour?.support_proration || false,
    },
    display_behaviour: {
      show_zero: Boolean(formData.showZero),
      visible_to_employee: Boolean(formData.visibleToEmployee),
      display_name: formData.displayName!,
    },
    pf_behaviour: {
      include_in_pf_wages: Boolean(formData.pf_behaviour?.include_in_pf_wages),
    },
    esi_behaviour: {
      include_in_esi_wages: Boolean(formData.esi_behaviour?.include_in_esi_wages),
    },
    pt_behaviour: {
      include_in_pt_wages: Boolean(formData.pt_behaviour?.include_in_pt_wages),
    },
    lwf_behaviour: {
      include_in_lwf_wages: Boolean(formData.lwf_behaviour?.include_in_lwf_wages),
    },
    gratuity_behaviour: {
      include_in_gratuity_wages: Boolean(formData.gratuity_behaviour?.include_in_gratuity_wages),
    },
    leave_encashment_behaviour: {
      include_in_leave_encashment_wages: Boolean(
        formData.leave_encashment_behaviour?.include_in_leave_encashment_wages,
      ),
    },
    ...(includeBaseCompensationBehaviour
      ? {
          base_compensation_behaviour: {
            include_in_base_compensation: Boolean(
              formData.base_compensation_behaviour?.include_in_base_compensation,
            ),
          },
        }
      : {}),
  };

  return {
    salary_component_requests: [
      {
        name: formData.name!,
        id,
        description: formData.description || '',
        category: 'FBP',
        is_active: Boolean(formData.isActive),
        component_type: formData.componentType || 'COMPONENT_TYPE_FBP',
        region: 'IN',
        request_precision:
          isFbpPercentagePrecisionEnabled && isPercentageOfBasicValue
            ? FBP_BPS_PRECISION
            : undefined,
        settings,
      },
    ],
  };
};
